import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../config/firebase';
import { Home, Store, CalendarDays, User } from 'lucide-react';

/**
 * BottomNav — the app-shell tab bar (Jose 2026-09-18).
 * BEHAVIOR-PIN: turns the customer portal into a real app shell. Persistent bottom tabs;
 * shows only on the shell routes when signed in. Tabs are clearly LABELED (Jose: clients must
 * know what each icon does). "Account" (NOT "Me") is the hub that owns Profile/Settings/Sign-Out
 * — the three icons that used to live in the header.
 */

const TABS = [
  { label: 'Home',     icon: Home,          to: '/dashboard' },
  { label: 'Block',    icon: Store,         to: '/block' },
  { label: 'Bookings', icon: CalendarDays,  to: '/appointments' },
  { label: 'Account',  icon: User,          to: '/account' },
];

// Only show the shell on these top-level destinations (not inside booking flows, login, etc.).
const SHELL_ROUTES = new Set(TABS.map(t => t.to));

const BottomNav: React.FC = () => {
  const [user] = useAuthState(auth);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  if (!user) return null;
  if (!SHELL_ROUTES.has(pathname)) return null;

  return (
    <nav
      data-tour="appnav"
      className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 flex justify-around items-stretch"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {TABS.map((tab) => {
        const active = pathname === tab.to;
        const Icon = tab.icon;
        return (
          <button
            key={tab.to}
            onClick={() => navigate(tab.to)}
            className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 transition-colors ${
              active ? 'text-slik-purple' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {tab.to === '/block' && (
              <span className="absolute top-1.5 right-[calc(50%-18px)] w-1.5 h-1.5 rounded-full bg-slik-green" />
            )}
            <Icon className="w-6 h-6" strokeWidth={active ? 2.4 : 2} />
            <span className={`text-[10px] ${active ? 'font-bold' : 'font-medium'}`}>{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
};

export default BottomNav;
